import { useQuery } from "urql";
import {
  GetFeedVolumeChartData,
  type GetFeedVolumeChartVariablesType,
  type NewsFeedChartData,
} from "@/data-access/news-feeds/queries/news-feed-volume-graph-queries";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import {
  normalizeStackByToEnum,
  transformFeedVolumeDataWithEnums,
} from "./feedVolumeGraphHelpers";

export const useNewsFeedVolumeChartData = (
  stackFeedVolumeBy: string | null,
) => {
  const {
    feedId,
    originalStartDate: formattedStartDate,
    originalEndDate: formattedEndDate,
    selectedFilters,
    period,
    filterDays,
    isCustomDaysRange,
  } = useAnalysisParamsForQueries();

  const filterDict = useFilterDictionary((state) => state);

  // Normalize to enum format for GraphQL query (v2 chart uses new schema)
  const normalizedStackBy = normalizeStackByToEnum(stackFeedVolumeBy);

  const input: GetFeedVolumeChartVariablesType["input"] = {
    period,
    daysSince: isCustomDaysRange ? null : (filterDays ?? null),
    stackBy: normalizedStackBy,
  };

  // Use original date range so selecting a bar does not refetch the chart.
  // When using preset range (daysSince), backend derives range from input; don't send dateRange in filters.
  // When using custom range, send startDate/endDate in filters and daysSince is null in input.
  const dateRange = isCustomDaysRange
    ? {
        startDate: formattedStartDate ?? null,
        endDate: formattedEndDate ?? null,
      }
    : { startDate: null, endDate: null };

  const filters: GetFeedVolumeChartVariablesType["filters"] = {
    feedId,
    dateRange,
    ...(selectedFilters ?? {}),
  };

  const [result] = useQuery({
    query: GetFeedVolumeChartData,
    variables: {
      input,
      filters,
    },
    pause: !feedId || !formattedStartDate || !formattedEndDate,
  });

  const { data, fetching } = result;

  const feedVolume = !data?.newsFeedChartV2
    ? []
    : transformFeedVolumeDataWithEnums(
        (data.newsFeedChartV2 as NewsFeedChartData[]).map((item) => ({
          ...item,
          chartData: (item.chartData as Record<string, number> | null) ?? {},
        })),
        stackFeedVolumeBy,
        filterDict,
      );

  return {
    feedVolume,
    fetching,
  };
};
